import { useMemo } from "react";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import { getShiftGroupConfig } from "@/utils/shiftgb-name";

export interface ShiftItem {
  shift_id: string;
  sequence?: number;
  default_shift?: string | null;
}

/** shift_gb / default_shift로 근무 띠(데이→…→오프) 순서 비교용 */
export type ShiftWithBand = ShiftItem & {
  id?: number;
  shift_gb?: string | null;
};

/**
 * 근무 띠 고정 순서: 데이 → 이브닝 → 나이트 → 미드 → 오프 → (그 외).
 * shift_gb 우선, 없으면 default_shift D/E/N/M/O 폴백.
 */
export function getShiftBandSortIndex(shift: ShiftWithBand): number {
  const rawGb = (shift.shift_gb ?? "").trim();
  if (rawGb) {
    const gbl = rawGb.toLowerCase();
    const bandByExact: [number, string[]][] = [
      [0, ["데이", "d", "day"]],
      [1, ["이브닝", "e", "evening", "eve"]],
      [2, ["나이트", "n", "night"]],
      [3, ["미드", "m", "mid"]],
      [4, ["오프", "o", "off"]],
    ];
    for (const [idx, labels] of bandByExact) {
      for (const lab of labels) {
        if (lab.length === 1) {
          if (gbl === lab) return idx;
        } else if (rawGb === lab || gbl === lab.toLowerCase()) {
          return idx;
        }
      }
    }
    if (rawGb.includes("데이")) return 0;
    if (rawGb.includes("이브")) return 1;
    if (rawGb.includes("나이트")) return 2;
    if (rawGb.includes("미드")) return 3;
    if (rawGb.includes("오프")) return 4;
  }
  const ds = shift.default_shift;
  if (ds === "D") return 0;
  if (ds === "E") return 1;
  if (ds === "N") return 2;
  if (ds === "M") return 3;
  if (ds === "O") return 4;
  return 100;
}

/**
 * 헤더·통계 열 공통: shift_gb(띠) 순 → sequence → id.
 * 동일 shift_id가 여러 행이어도 id로 안정 정렬.
 */
export function sortShiftsByShiftGbThenSequence<T extends ShiftWithBand>(
  shifts: T[]
): T[] {
  if (!shifts?.length) return [];
  return [...shifts].sort((a, b) => {
    const band = getShiftBandSortIndex(a) - getShiftBandSortIndex(b);
    if (band !== 0) return band;
    const sq = (a.sequence ?? 0) - (b.sequence ?? 0);
    if (sq !== 0) return sq;
    const aid =
      a.id != null && Number.isFinite(Number(a.id)) ? Number(a.id) : 0;
    const bid =
      b.id != null && Number.isFinite(Number(b.id)) ? Number(b.id) : 0;
    return aid - bid;
  });
}

/** `getDefaultShiftsSortedBySequence`과 동일 기준: default_shift 있고 서브코드(주 등) 아님 */
export function isMainTemplateShift(shift: {
  default_shift?: string | null;
}): boolean {
  const config = getShiftGroupConfig();
  if (
    !shift.default_shift ||
    config.SUB_CODE_LIST.includes(shift.default_shift)
  ) {
    return false;
  }
  return true;
}

/**
 * 근무표에 실제 사용된 시프트 행만 쓸 때 정렬:
 * default_shift 메인 템플릿 행 먼저 → shift_gb 띠 → sequence → id
 */
export function sortDisplayShiftsUsedOnlyOrder<T extends ShiftWithBand>(
  used: T[]
): T[] {
  if (!used?.length) return [];
  return [...used].sort((a, b) => {
    const aM = isMainTemplateShift(a);
    const bM = isMainTemplateShift(b);
    if (aM !== bM) return aM ? -1 : 1;
    const band = getShiftBandSortIndex(a) - getShiftBandSortIndex(b);
    if (band !== 0) return band;
    const sq = (a.sequence ?? 0) - (b.sequence ?? 0);
    if (sq !== 0) return sq;
    const aid =
      a.id != null && Number.isFinite(Number(a.id)) ? Number(a.id) : 0;
    const bid =
      b.id != null && Number.isFinite(Number(b.id)) ? Number(b.id) : 0;
    return aid - bid;
  });
}

// 하위 호환: default_shift 없는 데이터용 (useVisibleShifts, ViewTab2 등)
export const FIXED_SHIFTS = ["D", "E", "N", "O"] as const;

/** D/E/N/O(·M) 메인 시프트 우선 → 나머지 sequence 순. 훅 없이 쓸 때(통계 열 공통 계산 등). */
export function sortShiftsMainFirst<T extends ShiftItem>(shifts: T[]): T[] {
  if (!shifts || shifts.length === 0) return [];

  const mainShifts = getDefaultShiftsSortedBySequence(
    shifts as (T & { default_shift: string | null })[]
  );
  const mainIds = new Set(mainShifts.map((s) => s.shift_id));
  const otherShifts = shifts
    .filter((s) => !mainIds.has(s.shift_id))
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

  return [...mainShifts, ...otherShifts];
}

/** shift 목록 정렬 — shift_gb(데이→…→오프) 우선, 메인퍼스트 정렬은 열 공통 훅과 동일 축 */
export function useSortShift<T extends ShiftItem>(shifts: T[]) {
  return useMemo(
    () => sortShiftsByShiftGbThenSequence(shifts as ShiftWithBand[]),
    [shifts]
  );
}
